import React, { useState, useEffect, useRef } from "react";
import { Modal, IconButton, PrimaryButton, DefaultButton } from "@fluentui/react";
import { Icon } from "@fluentui/react/lib/Icon";
import styles from "./CopilotSelector.module.css";

interface Copilot {
    id: string;
    name: string;
    role: string;
    avatar: string;
    altText: string;
    profile: string;
    capabilities: string[];
    tone: string;
}

interface CopilotSelectorProps {
    isOpen: boolean;
    onDismiss: () => void;
    onCopilotSelect: (copilotId: string) => void;
    selectedCopilotId?: string;
}

const copilots: Copilot[] = [
    {
        id: "firulai",
        name: "FirulAI",
        role: "El Sabueso Experto",
        avatar: "/firulAI_copilot.png",
        altText: "Avatar de FirulAI",
        profile: "Tu aliado incondicional en la búsqueda del auto perfecto. Combina lealtad de perro, astucia de experto y tenacidad de sabueso. Procesa miles de datos en segundos para encontrar las mejores gangas.",
        capabilities: [
            "Rastrear el mercado en segundos para encontrar las mejores oportunidades.",
            "Detectar 'datos colmilludos' sobre precio y mantenimiento.",
            "Protegerte de malas compras identificando problemas antes que vos.",
            "Guiarte con confianza: 'Tranquilo, yo me encargo'."
        ],
        tone: "Decidido, protector y astuto. Siempre enfocado en conseguirte la mejor jugada."
    },
    {
        id: "don-trato",
        name: "Don Trato",
        role: "El Genio Reseller del Mercado Nica",
        avatar: "/revendedor.png",
        altText: "Avatar de Don Trato",
        profile: "Con más de 25 años de experiencia comprando y vendiendo autos en el mercado nicaragüense. Es el que sabe cómo se mueve la plaza, qué precios son reales y dónde se esconden las gangas.",
        capabilities: [
            "Evaluar si el precio de un carro es justo o inflado.",
            "Detectar oportunidades de reventa con buen margen.",
            "Darte tips de negociación para cerrar el trato a tu favor.",
            "Contarte trucos del mercado local: temporadas, ferias y lotes de confianza."
        ],
        tone: "Astuto, confiable y con ese aire de \"colmilludo\" que solo da la experiencia."
    },
    {
        id: "profe-ahorro",
        name: "Profe Ahorro",
        role: "La Maga de los Números",
        avatar: "/genio_num.png",
        altText: "Avatar de Profe Ahorro",
        profile: "Una genia matemática apasionada por los carros y obsesionada con los ahorros. Domina desde cálculos de depreciación hasta costos ocultos en mantenimiento.",
        capabilities: [
            "Calcular el costo total de propiedad (gasolina, repuestos, seguro, impuestos).",
            "Mostrarte comparativas entre modelos para que veas cuál rinde más por tu dinero.",
            "Encontrar formas de ahorrar a corto y largo plazo.",
            "Convertir datos complejos en consejos claros y prácticos."
        ],
        tone: "Inteligente pero cercana, tipo profe buena onda que quiere que siempre ganes más y gastes menos."
    },
    {
        id: "pofi",
        name: "El Pofi",
        role: "El Fanático de los Carros (moridor)",
        avatar: "/elpofi.png",
        altText: "Avatar de El Pofi",
        profile: "Tu brother de toda la vida, un verdadero car lover desde chavalo. Vive, sueña y respira carros. Es moridor: le apasiona tanto que lo transmite con emoción y energía.",
        capabilities: [
            "Ser tu copiloto emocional en la compra de tu carro.",
            "Guiarte paso a paso en el proceso de compra para que no te sientas solo.",
            "Explicarte detalles técnicos de forma simple y apasionada.",
            "Recomendaciones honestas: qué carro amarás manejar y cuál te dará problemas."
        ],
        tone: "Amigable, entusiasta y leal; nunca te va a fallar, siempre te va a aconsejar como un brother."
    }
];

export const CopilotSelector: React.FC<CopilotSelectorProps> = ({
    isOpen,
    onDismiss,
    onCopilotSelect,
    selectedCopilotId
}) => {
    const [selectedId, setSelectedId] = useState<string | undefined>(selectedCopilotId);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const modalRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<any>(null);
    const touchStartX = useRef<number>(0);
    const touchEndX = useRef<number>(0);

    // Handle window resize and viewport height
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);

            // Set CSS custom property for actual viewport height (for browsers without dvh support)
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };

        // Initial call
        handleResize();

        window.addEventListener('resize', handleResize);
        // Also listen to orientationchange for better mobile support
        window.addEventListener('orientationchange', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    // Focus management
    useEffect(() => {
        if (isOpen && closeButtonRef.current) {
            // Use setTimeout to ensure the modal is fully rendered
            setTimeout(() => {
                try {
                    // Try to focus the close button
                    if (closeButtonRef.current && typeof closeButtonRef.current.focus === 'function') {
                        closeButtonRef.current.focus();
                    }
                } catch (e) {
                    // Fallback: focus the modal container
                    modalRef.current?.focus();
                }
            }, 100);
        }
    }, [isOpen]);

    // Handle ESC key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onDismiss();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [isOpen, onDismiss]);

    const handleSelect = (id: string) => {
        setSelectedId(id);
    };

    const handleAccept = () => {
        if (selectedId) {
            onCopilotSelect(selectedId);
            onDismiss();
        }
    };

    const goToPrevious = () => {
        setCurrentIndex((prev) => (prev - 1 + copilots.length) % copilots.length);
    };

    const goToNext = () => {
        setCurrentIndex((prev) => (prev + 1) % copilots.length);
    };

    const goToSlide = (index: number) => {
        setCurrentIndex(index);
    };

    // Touch swipe handlers for mobile carousel
    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        const swipeThreshold = 50; // Minimum swipe distance
        const swipeDistance = touchStartX.current - touchEndX.current;

        if (Math.abs(swipeDistance) > swipeThreshold) {
            if (swipeDistance > 0) {
                // Swiped left - go to next
                goToNext();
            } else {
                // Swiped right - go to previous
                goToPrevious();
            }
        }

        // Reset
        touchStartX.current = 0;
        touchEndX.current = 0;
    };

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            if (e.key === 'ArrowLeft') {
                goToPrevious();
            } else if (e.key === 'ArrowRight') {
                goToNext();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={onDismiss}
            isBlocking={true}
            containerClassName={styles.modalContainer}
            styles={isMobile ? {
                // Let CSS handle mobile styles completely
                main: {
                    width: '100vw',
                    maxWidth: '100vw',
                    height: '100dvh',
                    maxHeight: '100dvh',
                    margin: 0,
                    borderRadius: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                },
                scrollableContent: {
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '100%'
                }
            } : {
                main: {
                    width: window.innerWidth < 900 ? '95vw' : '90vw',
                    maxWidth: window.innerWidth < 900 ? '600px' : '720px',
                    maxHeight: window.innerWidth < 900 ? '90vh' : '85vh',
                    borderRadius: '16px',
                    overflow: 'hidden'
                }
            }}
        >
            <div
                ref={modalRef}
                className={styles.modalContent}
                role="dialog"
                aria-modal="true"
                aria-labelledby="copilot-modal-title"
            >
                <div className={styles.modalHeader}>
                    <h2 id="copilot-modal-title" className={styles.modalTitle}>
                        Elige tu Copiloto
                    </h2>
                    <IconButton
                        componentRef={closeButtonRef}
                        className={styles.closeButton}
                        iconProps={{ iconName: 'Cancel' }}
                        ariaLabel="Cerrar"
                        onClick={onDismiss}
                    />
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.carouselContainer}>
                        <button
                            className={styles.carouselButton}
                            onClick={goToPrevious}
                            aria-label="Copiloto anterior"
                            tabIndex={0}
                        >
                            <Icon iconName="ChevronLeft" />
                        </button>

                        <div
                            className={styles.carouselTrack}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                        >
                            <div
                                className={styles.carouselSlides}
                                style={{
                                    transform: `translateX(-${currentIndex * 100}%)`
                                }}
                            >
                                {copilots.map((copilot, index) => (
                                    <div
                                        key={copilot.id}
                                        className={styles.carouselSlide}
                                        aria-hidden={index !== currentIndex}
                                    >
                                        <div
                                            className={`${styles.copilotCard} ${selectedId === copilot.id ? styles.selected : ''}`}
                                            onClick={() => handleSelect(copilot.id)}
                                            role="radio"
                                            aria-checked={selectedId === copilot.id}
                                            tabIndex={index === currentIndex ? 0 : -1}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleSelect(copilot.id);
                                                }
                                            }}
                                        >
                                            <div className={styles.cardHeader}>
                                                <img
                                                    src={copilot.avatar}
                                                    alt={copilot.altText}
                                                    className={styles.avatar}
                                                />
                                                <div className={styles.cardInfo}>
                                                    <h3 className={styles.copilotName}>{copilot.name}</h3>
                                                    <p className={styles.copilotRole}>{copilot.role}</p>
                                                </div>
                                            </div>

                                            <div className={styles.cardContent}>
                                                <div className={styles.profileSection}>
                                                    <h4 className={styles.sectionTitle}>Perfil</h4>
                                                    <p className={styles.profileText}>{copilot.profile}</p>
                                                </div>

                                                <div className={styles.capabilitiesSection}>
                                                    <h4 className={styles.sectionTitle}>Qué puede hacer por ti</h4>
                                                    <ul className={styles.capabilitiesList}>
                                                        {copilot.capabilities.map((capability, idx) => (
                                                            <li key={idx}>{capability}</li>
                                                        ))}
                                                    </ul>
                                                </div>

                                                <div className={styles.toneSection}>
                                                    <h4 className={styles.sectionTitle}>Personalidad</h4>
                                                    <p className={styles.toneText}>{copilot.tone}</p>
                                                </div>
                                            </div>

                                            <div className={styles.cardActions}>
                                                <button
                                                    className={`${styles.selectButton} ${selectedId === copilot.id ? styles.selectedButton : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelect(copilot.id);
                                                    }}
                                                    onTouchStart={(e) => e.stopPropagation()}
                                                    onTouchMove={(e) => e.stopPropagation()}
                                                    onTouchEnd={(e) => e.stopPropagation()}
                                                    aria-label={`Seleccionar ${copilot.name}`}
                                                >
                                                    {selectedId === copilot.id ? (
                                                        <>
                                                            <Icon iconName="CheckMark" className={styles.checkIcon} />
                                                            Seleccionado
                                                        </>
                                                    ) : (
                                                        'Seleccionar'
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button
                            className={styles.carouselButton}
                            onClick={goToNext}
                            aria-label="Siguiente copiloto"
                            tabIndex={0}
                        >
                            <Icon iconName="ChevronRight" />
                        </button>
                    </div>

                    <div className={styles.carouselIndicators}>
                        {copilots.map((copilot, index) => (
                            <button
                                key={index}
                                className={`${styles.indicator} ${index === currentIndex ? styles.indicatorActive : ''}`}
                                onClick={() => goToSlide(index)}
                                aria-label={`Ir a ${copilot.name}`}
                                style={{
                                    backgroundImage: `url(${copilot.avatar})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}
                            />
                        ))}
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <PrimaryButton
                        text="Aceptar"
                        onClick={handleAccept}
                        disabled={!selectedId}
                        className={styles.primaryButton}
                    />
                    <DefaultButton
                        text="Cancelar"
                        onClick={onDismiss}
                        className={styles.secondaryButton}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default CopilotSelector;